import React, { useEffect, useState } from "react";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import AppInput from "components/Input/AppInput";
import { Box, FormGroup, makeStyles, Typography } from "@material-ui/core";
import { YupValidations } from "utils/validate";
import AppColor from "utilities/AppColor";
import AppButton from "components/CustomButtons/AppButton";
// import { Link } from "react-router-dom";
import InputAutocompleteAddress from "components/Input/InputAutocompleteAddress";
import InputUpload from "components/Input/InputUpload";
import AppConfig from 'config/AppConfig';
import { useSelector } from "react-redux";
import { getAuthInfo } from "store/slices/auth";
import { SYSTEM_ROLE } from "config/AppConfig";
import AppUtil from "utilities/AppUtil";

const { PATTERNS, APPROVAL_STATUS } = AppConfig;
const maskNumber = "999999999999999";
const maskNumber10 = "9999999999";

const PersonalInformationForm = (props) => {
  const {
    onSubmit,
    isSubmitting,
    companyDetail = {},
    changeProgressStatus,
    display,
    hidePersonalInfo,
    // hideLeftControl,
    disabled,
    disabledContactInfo,
    isCreate,
  } = props;

  const { t } = useTranslation();
  const authInfo = useSelector(getAuthInfo) || {};
  const isMobile = AppUtil.mobileAndTabletCheck();

  const [initForm, setInitForm] = useState({
    email: "",
    emailCompany: "",
    firstName: "",
    lastName: "",
    organizationName: "",
    address1: "",
    address2: "",
    postalCode: "",
    phone: "",
    logo: "",
    domain: "",
    latlng: {},
    isShowRateForStaff: false
  });

  const [latLng, setLatLng] = useState({});
  const [isDisabledDomain, setIsDisabledDomain] = useState(false);

  const { company = {}, companyUser = {} } = companyDetail;

  const {
    id: companyId,
    organizationName = "",
    address1 = "",
    address2 = "",
    postalCode = "",
    phone = "",
    email: emailCompany,
    // logo = "",
    logoAws3Link = "",
    domain = "",
    approvalStatus,
    isShowRateForStaff
  } = company;

  const { lat, lng } = latLng || {};

  const { email = "", firstName = "", lastName = "", password = "" } = companyUser || {};

  const validationSchema = Yup.object().shape({
    email: Yup.string().trim()
      .required('validation.email.required')
      // .email('validation.email.invalid')
      .max(255, { message: 'validation.max_length', value: 255 })
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    emailCompany: Yup.string().trim()
      .required('validation.email.required')
      // .email('validation.email.invalid')
      .max(255, { message: 'validation.max_length', value: 255 })
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    firstName: Yup.string().trim().required('validation.first_name.required').max(64, "validation.max_length_64")
      // .matches(PATTERNS.XSS_PATTERN, 'validation.xssPatterns')
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    lastName: Yup.string().trim().required('validation.first_name.required').max(64, "validation.max_length_64")
      // .matches(PATTERNS.XSS_PATTERN, 'validation.xssPatterns') 
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    domain: Yup.string().trim()
      .required('validation.domain.required')
      .max(64, "validation.max_length_64")
      .matches(PATTERNS.DOMAIN_PATTERN, 'validation.domain.invalid')
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    address1: Yup.string()
      .trim()
      .required("validation.required")
      .max(240, "validation.max_length_240")
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces')
      .test('address1', 'toast.error.lat_lng', value => {
        if ((value !== company.address1) && !latLng.lat && !latLng.lng) {
          return false;
        } else {
          return true;
        }
      }),
    address2: Yup.string().max(240, "validation.max_length_240").nullable(),
    organizationName: Yup.string().trim().required('validation.first_name.required').max(255, "validation.max_length_255")
      // .matches(PATTERNS.XSS_PATTERN, 'validation.xssPatterns')
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    postalCode: Yup.number()
      .required("validation.required")
      .test('len', "validation.max_length_10", val => { return Math.ceil(Math.log10(val + 1)) <= 10 })
      .test('value', 'toast.error.924', value => {
        if (value !== Number(company.postalCode) && !latLng.lat && !latLng.lng) {
          return false;
        } else {
          return true;
        }
      }),
    phone: YupValidations.phone,
    password: (isCreate ? !password ? !email ? YupValidations.password.test('password', 'validation.password.spaces', password => {
      if (password && password.includes(' ')) {
        return false;
      } else {
        return true;
      }
    }) : '' : '' : ''),
    abn: Yup.number().nullable()
    // .test('len', "validation.max_length_10", val => { return Math.ceil(Math.log10(val + 1)) <= 10 })
  });

  const styles = makeStyles(() => ({
    loginContainer: {
      width: "calc(100% + 30px)",
      margin: "0 10px",
    },
    title: {
      display: "flex",
      margin: "2rem 0 1rem 0",
      "& h5": {
        color: AppColor.mainColor,
      },
    },
    control: {
      display: "flex",
      flexWrap: "wrap",
      flexDirection: "row",
      justifyContent: companyId ? "space-between" : "flex-end",
      "& a": {
        cursor: "pointer",
        textDecoration: "underline",
        color: AppColor.pink,
      },
    },
    inputUpLoad: {
      width: "100%",
    },
  }));

  const classes = styles();

  useEffect(() => {
    if (companyDetail && Object.keys(companyDetail).length > 0) {
      const initial = {
        email,
        emailCompany,
        firstName,
        lastName,
        organizationName,
        address1,
        address2,
        postalCode,
        phone,
        logo: logoAws3Link,
        domain,
        isShowRateForStaff
      };
      Object.keys(initial).forEach((key) => {
        if (initial[key] === null) {
          initial[key] = '';
        }
      })
      setInitForm(initial);
      if (companyDetail.company.lat && companyDetail.company.lng) {
        setLatLng({
          lat: companyDetail.company.lat,
          lng: companyDetail.company.lng
        });
      }
      setTimeout(() => {
        validationSchema.isValid(initial).then((valid) => {
          changeProgressStatus(0, valid);
        });
      }, 100);
    }
    if (authInfo.role === SYSTEM_ROLE.COMPANY && approvalStatus === APPROVAL_STATUS.ACTIVE) {
      setIsDisabledDomain(true);
    } else {
      setIsDisabledDomain(false);
    }
  }, [companyDetail]);

  const handleSubmitForm = (form) => {
    const params = {
      ...form,
      lat,
      lng,
    };
    !!companyId && delete params.email;
    onSubmit(params);
  };

  return (
    <Formik
      enableReinitialize={true}
      onSubmit={handleSubmitForm}
      initialValues={initForm}
      validationSchema={validationSchema}
    >
      {({ setFieldValue }) => {
        // console.log("values_company: ", values);
        return (
          <Form className={`${!isMobile && !display && "hidden"}`}>
            {
              <React.Fragment>
                <Box className={classes.title}>
                  <Typography variant="h5">
                    {t("label.company_profile")}
                  </Typography>
                </Box>
                {/* {values.logo ? (
                  <Box display="flex" alignItems="center">
                    <a
                      style={{ marginLeft: "1rem" }}
                      href={
                        process.env.REACT_APP_UPLOAD_IMAGE +
                        "/upload/logo/" +
                        values.logo
                      }
                    ></a>
                  </Box>
                ) : (
                  ""
                )} */}
                <Field
                  component={InputUpload}
                  name="logo"
                  disabled={disabled}
                  accept="image/jpeg, image/jpg, image/png"
                  showIconUpload={true}
                  showIconDownload={false}
                />
                <Field
                  component={AppInput}
                  name="organizationName"
                  id="organizationName"
                  label={"label.organizationName"}
                  required
                  disabled={disabled || disabledContactInfo}
                />
                <Field
                  component={AppInput}
                  name="emailCompany"
                  label={"label.emailSender"}
                  required
                  disabled={disabled || disabledContactInfo}
                />
                <Field
                  component={AppInput}
                  name="domain"
                  label={"label.domain"}
                  required
                  disabled={disabled || disabledContactInfo || isDisabledDomain}
                />
                <Field
                  component={AppInput}
                  name="abn"
                  type="tel"
                  mask={maskNumber10}
                  maskChar=""
                  label={"label.abn"}
                  disabled={disabled || disabledContactInfo}
                />
                <Field
                  component={InputAutocompleteAddress}
                  name="address1"
                  label={"label.address1"}
                  onChangePostalCode={(postalcode) => {
                    setFieldValue("postalCode", postalcode);
                  }}
                  setLatLng={setLatLng}
                  onChangeLatLng={(latlng) => {
                    setLatLng(latlng);
                    setFieldValue("latlng", latlng);
                  }}
                  required
                  disabled={disabled || disabledContactInfo}
                />
                <Field
                  component={AppInput}
                  name="address2"
                  id="address2"
                  label={"label.address2"}
                  disabled={disabled || disabledContactInfo}
                />
                <Field
                  component={AppInput}
                  name="postalCode"
                  type="tel"
                  mask={maskNumber10}
                  maskChar=""
                  label={"label.postalCode"}
                  required
                  disabled={disabled || disabledContactInfo}
                />
                <Field
                  component={AppInput}
                  name="phone"
                  type="tel"
                  mask={maskNumber}
                  maskChar=""
                  id="phone"
                  label={"label.phone"}
                  required
                  disabled={disabled || disabledContactInfo}
                />
                <Field
                  component={AppInput}
                  name="isShowRateForStaff"
                  id="isShowRateForStaff"
                  type="switch"
                  label={t("label.showRateForStaff")}
                />
              </React.Fragment>
            }
            {!hidePersonalInfo && (
              <React.Fragment>
                <Box className={classes.title}>
                  <Typography variant="h5">
                    {t("label.user_profile")}
                  </Typography>
                </Box>
                <Field
                  component={AppInput}
                  name={"email"}
                  label={"label.email"}
                  required
                  disabled={!!companyId || disabled}
                />
                {(isCreate && !password && !email) && (
                  <Field
                    component={AppInput}
                    name={"password"}
                    label={"label.password"}
                    type={"password"}
                    // placeholder={!companyDetail.password ? "********" : ''}
                    required
                    disabled={!!companyId || disabled}
                  />
                )}
                <Field
                  component={AppInput}
                  name="firstName"
                  label={"label.firstName"}
                  required
                  disabled={disabled}
                />
                <Field
                  component={AppInput}
                  name="lastName"
                  label={"label.lastName"}
                  required
                  disabled={disabled}
                />
              </React.Fragment>
            )}
            {!disabled && (
              <FormGroup className={classes.control}>
                <AppButton
                  label={`${t("button.saveAndContinue")}`}
                  rounder
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                />
              </FormGroup>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

PersonalInformationForm.propTypes = {
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  isCreate: PropTypes.bool,
  disabled: PropTypes.bool,
  hidePersonalInfo: PropTypes.bool,
  hideLeftControl: PropTypes.bool,
  display: PropTypes.bool,
  disabledContactInfo: PropTypes.bool,
  type: PropTypes.string,
  companyDetail: PropTypes.object,
  changeProgressStatus: PropTypes.func,
};

export default PersonalInformationForm;
